import type { HeroAppId } from '@hero/schema';

export type { HeroAppId, HeroPlatform } from '@hero/schema';

export type WebSessionRecordingConfig = { maskAllInputs: boolean } | false;

export const posthogConfig = {
  /** Ingestion host — where the SDKs send events and ask for flags. */
  apiHost: 'https://us.i.posthog.com',
  /**
   * App / private-API host — the PostHog UI (deep links from the admin
   * panel) and the Query API (`POST /api/projects/:id/query`, HogQL)
   * the backend reads referral activity from.
   */
  appHost: 'https://us.posthog.com',
  groupTypes: { org: 'org' },
  personProfiles: 'identified_only' as const,
  featureFlagsRequestTimeoutMs: 10000,

  web: {
    autocapture: true,
    capturePageview: true,
    capturePageleave: true,
    sessionRecording: {
      dashboard: { maskAllInputs: false },
      admin_panel: { maskAllInputs: false },
      landing: false,
    } satisfies Partial<Record<HeroAppId, WebSessionRecordingConfig>>,
    /** Browser console logs in session replay (Inspector → Console). Landing off. */
    recordingConsoleLog: {
      dashboard: true,
      admin_panel: true,
      landing: false,
    } satisfies Partial<Record<HeroAppId, boolean>>,
  },

  mobile: {
    enableSessionReplay: true,
    enablePersistSessionIdAcrossRestart: true,
    sessionReplayConfig: {
      maskAllTextInputs: false,
      maskAllImages: false,
      captureLog: true,
      captureNetworkTelemetry: true,
      throttleDelayMs: 1000,
    },
  },
} as const;

/** Agents never mount the JS SDK; missing keys fall back to unmasked replay. */
export function getWebSessionRecording(app: HeroAppId): WebSessionRecordingConfig {
  const map = posthogConfig.web.sessionRecording as Partial<
    Record<HeroAppId, WebSessionRecordingConfig>
  >;
  return map[app] ?? { maskAllInputs: false };
}

export function getWebRecordingConsoleLog(app: HeroAppId): boolean {
  const map = posthogConfig.web.recordingConsoleLog as Partial<Record<HeroAppId, boolean>>;
  return map[app] ?? false;
}
